import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Chip,
  useMediaQuery,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import RepeatIcon from '@mui/icons-material/Repeat';
import { utcTimestampToTimeString } from '../../services/utilities';
import { getDeviceId } from '../../utils/deviceId';
import { formatProduct, getOrderStatusColor, formatPriceWithPrecision } from '../../utils/utilfunction';
import useTouchInteraction from '../../hooks/useTouchInteraction';
import { formatTimeToIST } from '../../utils/dateUtils';
import { useDispatch } from 'react-redux';
import { openOrderWindow } from '../../store/actions/orders';
import { mapOrderWindowData } from '../../services/utilities';
import ExchangeTag from '../Common/ExchangeTag';

// Apply price precision formatting for CDS/DCSX exchanges, same as desktop table configs
const formatOrderPriceDisplay = (item, value) => {
  if ((item.exchange === 'CDS' || item.exchange === 'DCSX') && item.price_precision !== undefined && item.price_precision !== '') {
    return `₹${formatPriceWithPrecision(value, item.price_precision, {
      showCurrency: false,
      showCommas: false,
      removeTrailingZeros: false
    })}`;
  }
  return `₹${value.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

const MobileOrderDetailsDialog = ({ order, onClose, isMobile,isPending,tab }) => {
  const dispatch = useDispatch();

  // Add touch interaction for close button
  const { handlers: closeHandlers, touchStates: closeTouchStates } = useTouchInteraction(onClose, true);

  // Add touch interaction for bottom button
  const { handlers: bottomButtonHandlers, touchStates: bottomButtonTouchStates } = useTouchInteraction(onClose, true);

  const handleRepeatClick = (e) => {
    e.stopPropagation();
    
    // Normalize order_type for SLM since OrderWindow expects STOPLOSSMARKET
    const repeatedOrder = {
      ...order,
      order_type: order.order_type === 'SLM' ? 'STOPLOSSMARKET' : order.order_type
    };

    const windowData = mapOrderWindowData(repeatedOrder, order.order_side);
    windowData.is_repeat = true;
    windowData.price = order.price;
    windowData.quantity = order.quantity;

    dispatch(openOrderWindow(windowData));
    onClose();
  };

  const showRepeatButton = tab === 'COMPLETE' || tab === 'TRADE';

  if (!order) return null;

  const formatTime = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatLastActivityTime = (nanoseconds) => {
    const milliseconds = Math.floor(nanoseconds / 1000000);
    return new Date(milliseconds).toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };
  
  const detailFields = [
    { label: 'Order ID', value: order.oms_order_id },
    {
      label: 'Price',
      value: tab !== "TRADE"
        ? formatOrderPriceDisplay(order, order.order_status === 'COMPLETE' ? order.average_price : order.price)
        : formatOrderPriceDisplay(order, order.trade_price)
    },
    ...(tab !== "TRADE" ? [
      { label: 'Tr. Price', value: order.trigger_price || 'N/A' },
      { label: 'Avg. Price', value: order.average_price || 'N/A' },
      { label: 'Disc. Qty', value: order.disclosed_quantity },
      { label: 'Client ID', value: order.client_id },
      { label: 'Order Type', value: order.order_type },
      { label: 'Product', value: `${formatProduct(order.product)}` },
      { label: 'Order Update Time', value: formatLastActivityTime(order.last_activity_reference) },
      { label: 'Exchange Time', value: formatTime(order.order_entry_time) },
      { label: 'Exchange Order ID', value: order.exchange_order_id },
      { label: 'Status', value: order.order_status }
    ] : [
      { label: 'Product', value: `${formatProduct(order.product)}` },
      { label: 'Exchange Time', value: formatTime(order.order_entry_time) },
      { label: 'Exchange Order ID', value: order.exchange_order_id }
    ])
  ];

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          ...(isMobile ? {
            height: '100%',
            maxHeight: '100%',
            borderRadius: 0
          } : {
            maxHeight: '90vh'
          })
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
          borderBottom: '3px solid',
          borderColor: order.order_side === 'BUY' ? 'success.main' : 'error.main',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: 'background.paper'
        }}
      >
        <Box sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          width: '100%',
          gap: isMobile ? 1 : 0
        }}>
          <Box>
            <Typography variant="h6">
              {order.trading_symbol}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                {order.order_side}
              </Typography>
              <Typography variant="caption" color="text.secondary">•</Typography>
              <ExchangeTag exchange={order.exchange} />
            </Box>
          </Box>
          <Typography variant="subtitle1">
          {isPending
                    ? `${order.filled_quantity || 0}/${order.quantity}`
                    : tab === "COMPLETE"
                      ? `${order.filled_quantity || 0}/${order.quantity}`
                      : order.trade_quantity || order.quantity}
          </Typography>
        </Box>
        <Box
          {...closeHandlers}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: closeTouchStates.isActive && !closeTouchStates.isTouchMoved ? 'action.hover' : 'transparent',
            transition: 'background-color 0.2s ease',
            WebkitTapHighlightColor: 'transparent',
            '&:active': {
              backgroundColor: !closeTouchStates.isTouchMoved ? 'action.hover' : 'transparent'
            }
          }}
        >
          <CloseIcon sx={{ 
            color: 'text.secondary',
            pointerEvents: 'none'
          }} />
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          mt: 2,
          overflowY: 'auto',
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
          gap: 2,
          p: isMobile ? 2 : 3
        }}
      >
        {detailFields.map((field, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              p: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              {field.label}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 'normal ' }}
            >
              {field.value}
            </Typography>
          </Box>
        ))}
      </DialogContent>

      {order.rejection_reason && order.rejection_reason !== 'NONE' && (
        <Box sx={{
          m: 2,
          p: 1.5,
          backgroundColor: 'rgb(229, 246, 253)',
          color: 'rgb(1, 67, 97)',
          borderRadius: 1,
        }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Rejection Reason: {order.rejection_reason}
          </Typography>
        </Box>
      )}

      <DialogActions sx={{
        p: 2,
        justifyContent: 'flex-end',
        borderTop: '1px solid',
        borderColor: 'divider',
        gap: 1
      }}>
        {showRepeatButton && (
          <Button
            variant="outlined"
            onClick={handleRepeatClick}
            fullWidth={isMobile}
            startIcon={<RepeatIcon />}
            sx={{
              flex: 1,
              borderColor: 'primary.main',
              color: 'primary.main',
            }}
          >
            Repeat
          </Button>
        )}
        <Button
          {...bottomButtonHandlers}
          variant="outlined"
          fullWidth={isMobile}
          sx={{
            flex: showRepeatButton ? 1 : undefined,
            backgroundColor: bottomButtonTouchStates.isActive && !bottomButtonTouchStates.isTouchMoved ? 'action.hover' : 'transparent',
            transition: 'background-color 0.2s ease',
            WebkitTapHighlightColor: 'transparent',
            '&:active': {
              backgroundColor: !bottomButtonTouchStates.isTouchMoved ? 'action.hover' : 'transparent'
            }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// const getOrderStatusColor = (status) => {
//   const statusColorMap = {
//     'COMPLETE': 'success.main',
//     'CANCELLED': 'error.main',
//     'CANCEL_CONFIRMED': 'error.main',
//     'REJECTED': 'error.main',
//     'PENDING': 'warning.main',
//     'EXCHANGE_RESPONSE_PENDING': 'warning.main',
//     'OPEN': 'warning.main',
//     'PARTIALLY_EXECUTED': 'warning.main'
//   };

//   return statusColorMap[status] || 'text.secondary';
// };

const TradeCard = ({ trade, isPending, tab }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clicked, setClicked] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const dispatch = useDispatch();

  // Add touch interaction for card click
  const handleCardClick = useCallback(() => {
    setSelectedOrder(trade);
  }, [trade]);

  const { handlers: cardHandlers, touchStates: cardTouchStates } = useTouchInteraction(handleCardClick, true);

  // Add touch interaction for info button
  const handleInfoClick = useCallback((e) => {
    e.stopPropagation();
    setSelectedOrder(trade);
  }, [trade]);

  const { handlers: infoHandlers, touchStates: infoTouchStates } = useTouchInteraction(handleInfoClick, true);

  // Add touch interaction for modify button
  const handleModifyClick = useCallback((e) => {
    e.stopPropagation();
    handleModifyOrder(trade, e);
  }, [trade]);

  const { handlers: modifyHandlers, touchStates: modifyTouchStates } = useTouchInteraction(handleModifyClick, true);

  // Add touch interaction for cancel button
  const handleCancelClick = useCallback(async (e) => {
    e.stopPropagation();
    if (clicked || trade.order_status === "EXCHANGE_RESPONSE_PENDING") return;
    setClicked(true);
    try {
      await deletePendingOrder(trade.oms_order_id, trade.execution, e);
    } catch (error) {
      console.error("Error deleting order:", error);
      setClicked(false);
    }
  }, [clicked, trade]);

  const { handlers: cancelHandlers, touchStates: cancelTouchStates } = useTouchInteraction(handleCancelClick, true);

  const handleModifyOrder = (order, e) => {
    e.stopPropagation();

    // Normalize order type and determine price/trigger price
    const normalizedOrderType =
      order.order_type === 'SLM' ? 'STOPLOSSMARKET' :
        order.order_type === 'SL' ? 'SL' :
          order.order_type;

    // Determine price and trigger price based on order type
    let price, triggerPrice;
    switch (order.order_type) {
      case 'SLM':
        price = 0;  // Always 0 for SLM
        triggerPrice = order.trigger_price || order.price || order.ltp || 0;
        break;
      case 'SL':
        price = order.price || order.ltp || 0;
        triggerPrice = order.trigger_price || order.price || order.ltp || 0;
        break;
      case 'LIMIT':
        price = order.price || order.ltp || 0;
        triggerPrice = 0;
        break;
      default:
        price = order.price || order.ltp || 0;
        triggerPrice = 0;
    }

    // Create modify order data
    const modifyData = {
      ...order,
      price: price,
      trigger_price: triggerPrice,
      order_type: normalizedOrderType,
      is_modify: true
    };

    // Use the Redux action directly
    dispatch(openOrderWindow(mapOrderWindowData(modifyData, order.order_side)));
  };

  const deletePendingOrder = async (oms_order_id, execution_type, e) => {
    e.stopPropagation();
    try {
      // Ensure the user is authenticated
      const loginId = localStorage.getItem('login_id');
      const token = localStorage.getItem('auth_token');
      if (!loginId) {
        throw new Error("User is not authenticated.");
      }

      // Call the DELETE API using fetch
      const response = await fetch(
        `/api/v1/orders/${oms_order_id}?client_id=${loginId}`,
        {
          method: "DELETE",
          headers: {
            'Content-Type': 'application/json',
            'x-authorization-token': token,
            'x-device-type': 'WEB',
            'x-device-id': getDeviceId()
          },
        }
      );

      const data = await response.json();

      if (data.status === "success") {
        setClicked(false)
      } else {
        throw new Error(data.message || "Failed to delete order.");
      }
    } catch (err) {
      setError(err.message || "Failed to delete order.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card
        {...cardHandlers}
        sx={{
          mb: 1,
          p: 0.5,
          borderLeft: trade.order_side === "BUY"
            ? `3px solid ${theme.palette.success.main}`
            : `3px solid ${theme.palette.error.main}`,
          position: 'relative',
          transition: 'transform 0.2s ease-in-out, background-color 0.2s ease',
          backgroundColor: cardTouchStates.isActive && !cardTouchStates.isTouchMoved ? 'action.hover' : 'background.paper',
          WebkitTapHighlightColor: 'transparent',
          '&:active': {
            backgroundColor: !cardTouchStates.isTouchMoved ? 'action.hover' : 'background.paper'
          }
        }}
      >
        <CardContent sx={{
          p: '8px !important',
          '&:last-child': {
            pb: '8px !important'
          }
        }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1" fontWeight="bold" noWrap sx={{ fontSize: '0.875rem' }}>
                  {trade.trading_symbol}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 'bold',
                    color: trade.order_side === "BUY" ? theme.palette.success.main : theme.palette.error.main,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    ml: 0.5
                  }}
                >
                  {trade.order_side}
                </Typography>
                <ExchangeTag exchange={trade.exchange} />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.675rem' }}
                >
                  {formatProduct(trade.product)}
                </Typography>
                {
                  trade.order_status && (
                    <> <Box
                      component="span"
                      sx={{
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        bgcolor: 'text.disabled',
                        mx: 0.5,
                        display: 'inline-block'
                      }}
                    />
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.675rem',
                          color: getOrderStatusColor(trade.order_status),
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        {trade.order_status}
                      </Typography></>)
                }

              </Box>
              {trade.order_status === 'REJECTED' && trade.rejection_reason && trade.rejection_reason !== 'NONE' && (
                <Typography
                  variant="caption"
                  color="error.main"
                  sx={{ 
                    fontSize: '0.65rem', 
                    mt: 0.25 
                  }}
                >
                  Rejection Reason: {trade.rejection_reason}
                </Typography>
              )}
            </Box>
            <IconButton
              {...infoHandlers}
              size="small"
              sx={{
                color: 'text.secondary',
                p: 0.5,
                backgroundColor: infoTouchStates.isActive && !infoTouchStates.isTouchMoved ? 'action.hover' : 'transparent',
                WebkitTapHighlightColor: 'transparent',
                '&:active': {
                  backgroundColor: !infoTouchStates.isTouchMoved ? 'action.hover' : 'transparent'
                }
              }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Box>

          <Divider />

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            mt: 0.5
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.625rem' }}>
                  Quantity
                </Typography>
                <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.75rem' }}>
                  {isPending
                    ? `${trade.filled_quantity || 0}/${trade.quantity}`
                    : tab === "COMPLETE"
                      ? `${trade.filled_quantity || 0}/${trade.quantity}`
                      : trade.trade_quantity || trade.quantity}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.625rem' }}>
                  Price
                </Typography>
                <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.75rem' }}>
                  {formatOrderPriceDisplay(
                    trade,
                    tab === "TRADE"
                      ? trade.trade_price
                      : trade.order_status === 'COMPLETE' ? trade.average_price : trade.price
                  )}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.625rem' }}>
                  Time
                </Typography>
                <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.75rem' }}>
                  {formatTimeToIST(trade.order_entry_time)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.625rem' }}>
                  Type
                </Typography>
                <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.75rem' }}>
                  {trade.order_type}
                </Typography>
              </Box>
            </Box>
            {isPending && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  alignItems: 'center'
                }}
              >
                <IconButton
                  {...modifyHandlers}
                  disabled={trade.order_status === "EXCHANGE_RESPONSE_PENDING"}
                  sx={{
                    color: trade.order_status === "EXCHANGE_RESPONSE_PENDING"
                      ? 'text.disabled'
                      : theme.palette.primary.main,
                    p: 0.5,
                    backgroundColor: modifyTouchStates.isActive && !modifyTouchStates.isTouchMoved
                      ? 'action.hover'
                      : 'rgba(0, 0, 0, 0.05)',
                    WebkitTapHighlightColor: 'transparent',
                    '&:active': {
                      backgroundColor: !modifyTouchStates.isTouchMoved ? 'action.hover' : 'rgba(0, 0, 0, 0.05)'
                    }
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>

                <IconButton
                  {...cancelHandlers}
                  disabled={trade.order_status === "EXCHANGE_RESPONSE_PENDING"}
                  sx={{
                    color: trade.order_status === "EXCHANGE_RESPONSE_PENDING"
                      ? 'text.disabled'
                      : theme.palette.error.main,
                    p: 0.5,
                    backgroundColor: cancelTouchStates.isActive && !cancelTouchStates.isTouchMoved
                      ? 'rgba(255, 0, 0, 0.1)'
                      : 'rgba(255, 0, 0, 0.05)',
                    WebkitTapHighlightColor: 'transparent',
                    '&:active': {
                      backgroundColor: !cancelTouchStates.isTouchMoved ? 'rgba(255, 0, 0, 0.1)' : 'rgba(255, 0, 0, 0.05)'
                    }
                  }}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {selectedOrder && (
        <MobileOrderDetailsDialog
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          isPending={isPending}
          isMobile={isMobile}
          tab={tab}
        />
      )}
    </>
  );
};

const TradeCards = ({ trades, isPending, tab }) => {
  return (
    <Box 
      sx={{ 
        px: 1, 
        py: 1, 
        height: '100%', 
        overflowY: 'auto',  // Enable vertical scrolling
        pb: 15  // Add extra padding at the bottom to ensure last card is fully visible
      }}
    >
      {trades.map((trade) => (
        <TradeCard 
          key={trade.tradeId} 
          trade={trade} 
          isPending={isPending} 
          tab={tab} 
        />
      ))}
    </Box>
  );
};

export default TradeCards;
